/**
 * Operational store status — shared between By Store and Progress/map views.
 */

import type { TrackerOperationalStatus, TrackerRouteStatus } from './tracker-types.js';

export function computeOperationalStatus(input: {
  totalTasks: number;
  pendingTaskCount: number;
}): TrackerOperationalStatus {
  if (input.totalTasks === 0 || input.pendingTaskCount >= input.totalTasks) {
    return 'NOT_STARTED';
  }
  if (input.pendingTaskCount === 0) {
    return 'COMPLETE';
  }
  return 'IN_PROGRESS';
}

/** Whether the operator has acted on this task (fieldwork submitted or flagged). */
export function isTrackerTaskSubmitted(input: {
  hasPostingEvent: boolean;
  rejectionReason?: string | null;
  resolvedByOverrideId?: number | null;
  taskStatus?: string | null;
}): boolean {
  if (input.rejectionReason?.trim()) {
    return true;
  }
  if (input.resolvedByOverrideId != null) {
    return true;
  }
  if (input.hasPostingEvent) {
    return true;
  }
  const status = input.taskStatus ?? '';
  return (
    status === 'operator_complete'
    || status === 'admin_complete'
    || status === 'approved'
    || status === 'verified'
    || status === 'admin_rejected'
  );
}

/** Map operational status to legacy route status for backward compatibility. */
export function operationalStatusToLegacyRouteStatus(
  operationalStatus: TrackerOperationalStatus,
): TrackerRouteStatus {
  switch (operationalStatus) {
    case 'NOT_STARTED':
      return 'PENDING';
    case 'IN_PROGRESS':
      return 'ACTIVE';
    case 'COMPLETE':
      return 'DONE';
    default: {
      const exhaustive: never = operationalStatus;
      return exhaustive;
    }
  }
}

export const OPERATIONAL_STATUS_COLORS: Record<TrackerOperationalStatus, string> = {
  NOT_STARTED: '#6b7280',
  IN_PROGRESS: '#3b82f6',
  COMPLETE: '#22c55e',
};

export const REJECTION_INDICATOR_COLOR = '#f97316';
